import React, { useState, useCallback, useRef, useEffect } from "react";
import "./DPad.css";

const DPad = ({ onMove }) => {
  const [activeDirection, setActiveDirection] = useState(null);
  const intervalRef = useRef(null);

  const handleDirectionPress = useCallback((direction) => {
    setActiveDirection(direction);
  }, []);

  const handleDirectionRelease = useCallback(() => {
    setActiveDirection(null);
  }, []);

  // Mobile reliability: if touchend happens outside the button, ensure we still release
  useEffect(() => {
    if (!activeDirection) return;

    const release = (e) => {
      handleDirectionRelease();
    };

    window.addEventListener("touchend", release, { passive: false });
    window.addEventListener("touchcancel", release, { passive: false });
    return () => {
      window.removeEventListener("touchend", release);
      window.removeEventListener("touchcancel", release);
    };
  }, [activeDirection, handleDirectionRelease]);

  // Movement control - send continuous signal while button is held
  // Throttling is handled in GameMap's handleDPad function
  useEffect(() => {
    if (!activeDirection) {
      // Stop movement when no direction is active
      if (onMove) {
        onMove({ x: 0, y: 0 });
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Map direction to movement values
    const movements = {
      up: { x: 0, y: -1.0 },
      down: { x: 0, y: 1.0 },
      left: { x: -1.0, y: 0 },
      right: { x: 1.0, y: 0 },
    };

    const movement = movements[activeDirection];

    // Send movement signal continuously while held
    // GameMap's handleDPad will throttle to prevent too-fast movement
    const sendMovement = () => {
      if (onMove && movement) {
        onMove(movement);
      }
    };

    // Send initial movement immediately
    sendMovement();

    // Continue sending at high frequency - throttling handled by GameMap
    intervalRef.current = setInterval(sendMovement, 50);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activeDirection, onMove]);

  // Mouse events
  const onMouseDown = useCallback((direction) => (e) => {
    e.preventDefault();
    handleDirectionPress(direction);
  }, [handleDirectionPress]);

  const onMouseUp = useCallback((e) => {
    e.preventDefault();
    handleDirectionRelease();
  }, [handleDirectionRelease]);

  // Touch events
  const onTouchStart = useCallback((direction) => (e) => {
    handleDirectionPress(direction);
  }, [handleDirectionPress]);

  const onTouchEnd = useCallback((e) => {
    handleDirectionRelease();
  }, [handleDirectionRelease]);

  return (
    <div className="dpad-container">
      <div className="dpad-base">
        {/* Center circle */}
        <div className="dpad-center" />
        
        {/* Up button */}
        <button
          className={`dpad-button dpad-up ${activeDirection === 'up' ? 'dpad-active' : ''}`}
          onMouseDown={onMouseDown('up')}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart('up')}
          onTouchEnd={onTouchEnd}
          aria-label="Move Up"
        >
          <div className="dpad-arrow dpad-arrow-up">▲</div>
        </button>

        {/* Right button */}
        <button
          className={`dpad-button dpad-right ${activeDirection === 'right' ? 'dpad-active' : ''}`}
          onMouseDown={onMouseDown('right')}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart('right')}
          onTouchEnd={onTouchEnd}
          aria-label="Move Right"
        >
          <div className="dpad-arrow dpad-arrow-right">▶</div>
        </button>

        {/* Down button */}
        <button
          className={`dpad-button dpad-down ${activeDirection === 'down' ? 'dpad-active' : ''}`}
          onMouseDown={onMouseDown('down')}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart('down')}
          onTouchEnd={onTouchEnd}
          aria-label="Move Down"
        >
          <div className="dpad-arrow dpad-arrow-down">▼</div>
        </button>

        {/* Left button */}
        <button
          className={`dpad-button dpad-left ${activeDirection === 'left' ? 'dpad-active' : ''}`}
          onMouseDown={onMouseDown('left')}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart('left')}
          onTouchEnd={onTouchEnd}
          aria-label="Move Left"
        >
          <div className="dpad-arrow dpad-arrow-left">◀</div>
        </button>
      </div>
    </div>
  );
};

export default DPad;
